import React, {
  createContext,
  Dispatch,
  ReactNode,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  createPortal,
} from 'react-dom';
import clsx from 'clsx';
import UploadIcon from 'core/icons/upload';
import {
  DropzoneAccept,
} from 'core/mimeMap';
import {
  useFileDropzone,
} from 'pages/Dashboard/pages/Billing/hooks';
import isNil from 'lodash/isNil';

export type DragRejectReason = 'multiple' | 'type' | null;

type ContextShape = {
  setUploading: Dispatch<SetStateAction<boolean>>;
  setEnabled: Dispatch<SetStateAction<boolean>>;
  isDragging: boolean;
  rejectReason: DragRejectReason;
  isError: boolean;
};

export const FileDragOverlayContext = createContext<ContextShape>({
  setUploading: () => {},
  setEnabled: () => {},
  isDragging: false,
  rejectReason: null,
  isError: false,
});

type Messages = {
  default?: string;
  multiple?: string;
  type?: string;
  uploading?: string;
  supportedHint?: string;
};

type Props = {
  children?: ReactNode;
  targetRef?: RefObject<HTMLElement | null>;
  enabled?: boolean;
  disabled?: boolean;
  onDropFile?: (file: File | File[]) => void;
  onReject?: (reason: DragRejectReason) => void;
  isUploading?: boolean;
  messages?: Messages;
  backgroundClassName?: string;
  dropzoneAccept?: DropzoneAccept;
  showOverlay?: boolean;
  multiple?: boolean;
  renderOverlayContent?: (
    args: {
      isError: boolean;
      message: string;
      messages?: Messages;
    },
  ) => ReactNode;
  animate?: boolean;
};

export default function FileDragOverlayProvider({
  children,
  targetRef,
  onDropFile,
  onReject,
  enabled: enabledProp = true,
  disabled: disabledProp = false,
  isUploading: isUploadingProp = false,
  messages,
  backgroundClassName = 'bg-white -mt-5',
  dropzoneAccept,
  showOverlay: _showOverlay = true,
  multiple = false,
  renderOverlayContent,
  animate = false,
}: Props) {
  const effectiveEnabled = enabledProp && !disabledProp;
  const [enabled, setEnabled] = useState<boolean>(effectiveEnabled);
  const [isUploading, setUploading] = useState<boolean>(isUploadingProp);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<DragRejectReason>(null);

  useEffect(() => { setEnabled(effectiveEnabled); }, [effectiveEnabled]);
  useEffect(() => { setUploading(isUploadingProp); }, [isUploadingProp]);

  const getRejectReason = useCallback((items: DataTransferItemList | null): DragRejectReason => {
    const count = items?.length ?? 0;
    if (count > 1 && !multiple) {
      return 'multiple';
    }

    if (!isNil(dropzoneAccept) && count > 0) {
      const acceptedMimeTypes = new Set(Object.keys(dropzoneAccept));
      const hasRejectedType = Array.from(items ?? []).some((item) => (
        item.kind === 'file'
        && !isNil(item.type)
        && item.type !== ''
        && !acceptedMimeTypes.has(item.type)
      ));

      if (hasRejectedType) {
        return 'type';
      }
    }

    return null;
  }, [dropzoneAccept, multiple]);

  const onDrop = useCallback((acceptedFiles: File[], fileRejections: any[]) => {
    setIsDragging(false);
    const [firstFile] = acceptedFiles ?? [];
    const fileCount = acceptedFiles?.length ?? 0;

    let reject: DragRejectReason = null;

    if (fileRejections?.length > 0) {
      reject = 'type';
    } else if (fileCount > 1 && !multiple) {
      reject = 'multiple';
    } else if (fileCount !== 1) {
      reject = null;
    }

    setRejectReason(reject);
    onReject?.(reject);

    if (isNil(reject) && firstFile) {
      onDropFile?.(multiple ? acceptedFiles : firstFile);
    }
  }, [multiple, onDropFile, onReject]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    isDragAccept,
    isMultipleDrag,
  } = useFileDropzone(
    onDrop,
    dropzoneAccept,
    multiple,
    !enabled,
  );

  useEffect(() => {
    if (isDragActive) {
      try {
        const activeElement = document.activeElement as HTMLElement | null;
        if (!isNil(activeElement)) {
          activeElement.blur();
        }
      } catch (e) {
        // no-op
      }
      setIsDragging(true);
      if (isMultipleDrag && !multiple) {
        setRejectReason('multiple');
      } else if (isDragReject) {
        setRejectReason('type');
      } else {
        setRejectReason(null);
      }
    } else {
      setIsDragging(false);
    }
  }, [isDragActive, isMultipleDrag, isDragReject, isDragAccept, multiple]);

  // Bootstrap visibility on initial hover so the catcher can start receiving events
  useEffect(() => {
    const node = (targetRef?.current as HTMLElement | null) ?? document;
    const hasFiles = (event: DragEvent) => Array.from(event?.dataTransfer?.types ?? []).includes('Files');

    const onFileEnter = (event: DragEvent) => {
      if (hasFiles(event)) {
        setIsDragging(true);
        setRejectReason(getRejectReason(event?.dataTransfer?.items ?? null));
      }
    };

    const onFileOver = (event: DragEvent) => {
      if (hasFiles(event)) {
        setIsDragging(true);
        setRejectReason(getRejectReason(event?.dataTransfer?.items ?? null));
      }
    };

    if (enabled && !isNil(node)) {
      node.addEventListener('dragenter', onFileEnter as any);
      node.addEventListener('dragover', onFileOver as any);
    }

    return () => {
      if (enabled && !isNil(node)) {
        node.removeEventListener('dragenter', onFileEnter as any);
        node.removeEventListener('dragover', onFileOver as any);
      }
    };
  }, [enabled, targetRef, getRejectReason]);

  // Fallback: ensure error/overlay dismiss when pointer leaves dialog or drag ends anywhere
  useEffect(() => {
    const node = targetRef?.current as HTMLElement | null;
    const onWindowDragStop = () => {
      setIsDragging(false);
    };
    const onLeave = () => {
      setIsDragging(false);
      setRejectReason(null);
    };
    if (enabled && !isNil(node)) {
      window.addEventListener('dragend', onWindowDragStop);
      window.addEventListener('drop', onWindowDragStop);
      node?.addEventListener('dragleave', onLeave);
      node?.addEventListener('mouseleave', onLeave);
    }
    return () => {
      if (enabled && !isNil(node)) {
        window.removeEventListener('dragend', onWindowDragStop);
        window.removeEventListener('drop', onWindowDragStop);
        node?.removeEventListener('dragleave', onLeave);
        node?.removeEventListener('mouseleave', onLeave);
      }
    };
  }, [enabled, targetRef]);

  const message = useMemo(() => {
    let effective = messages?.default ?? 'Drop file to upload';
    if (rejectReason === 'multiple' || (isMultipleDrag && !isDragAccept && !multiple)) {
      effective = messages?.multiple ?? 'Multiple files are not supported';
    } else if (isDragReject || rejectReason === 'type') {
      effective = messages?.type ?? 'File type not supported';
    } else if (isUploading) {
      effective = messages?.uploading ?? 'Uploading...';
    }
    return effective;
  }, [isDragReject, isMultipleDrag, isDragAccept, isUploading, messages, rejectReason]);

  const showOverlay = _showOverlay && (
    isDragging || isDragActive || isUploading || !isNil(rejectReason)
  );
  const isError = !isNil(rejectReason) || isDragReject || (isMultipleDrag && !multiple);

  const contextValue = useMemo(() => ({
    setUploading,
    setEnabled,
    isDragging,
    rejectReason,
    isError,
  }), [isDragging, rejectReason, isError]);

  return (
    <FileDragOverlayContext.Provider value={contextValue}>
      {children}
      {enabled && targetRef?.current && createPortal(
        (animate || showOverlay) ? (
          <div className={clsx(
            'absolute inset-0 z-[1400] flex items-center justify-center pointer-events-none',
            { 'opacity-0': animate && !showOverlay,
              'opacity-100': animate && showOverlay,
              'transition-opacity duration-200 ease-out': animate },
          )}
          >
            <div className={clsx(
              'absolute inset-0',
              backgroundClassName,
              { 'transition-opacity duration-200 ease-out': animate },
            )}
            />
            {!isNil(renderOverlayContent) ? (
              renderOverlayContent({ isError, message, messages: messages ?? {} })
            ) : (
              <div className={clsx(
                'relative flex flex-col items-center justify-center text-center gap-3 p-8 rounded-xl border-2 border-dashed bg-white shadow-xl',
                isError ? 'border-red-500' : 'border-blue-400',
              )}
              >
                <UploadIcon className={clsx('!w-10 !h-10', isError ? 'text-red-500' : 'text-blue-500')} />
                <div className={clsx('text-sm font-semibold', isError ? 'text-red-700' : 'text-gray-800')}>{message}</div>
                <div className="text-xs text-gray-500">{messages?.supportedHint ?? 'Supported: PDF, PNG, JPG, TIFF'}</div>
              </div>
            )}
          </div>
        ) : null,
        targetRef.current,
      )}
      {enabled && targetRef?.current && createPortal(
        <div
          {...getRootProps({
            className: clsx(
              'absolute inset-0 z-[1401]',
              (isDragging || isDragActive) ? 'pointer-events-auto' : 'pointer-events-none',
            ),
          })}
        >
          <input
            {...getInputProps({
              onClick: (e: any) => {
                e.preventDefault();
                e.stopPropagation();
              },
            })}
          />
        </div>,
        targetRef.current,
      )}
    </FileDragOverlayContext.Provider>
  );
}
